import {
  object as yupObject,
  string as yupString,
  number as yupNumber,
  array as yupArray,
} from 'yup';
import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';
import intersection from 'lodash/intersection';

import {
  exceptionBases,
  countryTaxTypes,
  US_COUNTRY_TAX_TYPE_REGEX,
  ALL_OPTION,
} from '../../shared/constants';

const exceptionBasisValues = exceptionBases.ALL.map(exceptionBasis => exceptionBasis.value);

const US_FEDERAL = countryTaxTypes.US2;

const ERROR_MESSAGES = {
  required: fieldName => () => `${fieldName} is required`,
  oneOf: fieldName => ({ values }) => `${fieldName} must be one of ${values}`,
};

const bookToTaxDifferenceSchema = yupObject().shape({
  connections: yupArray()
    .test('Connections', 'You must select Business Units or Connections but not both', (value, testContext) => {
      const isBusinessUnits = !!testContext.parent.businessUnits?.length;
      const isConnections = !!value?.length;
      return isBusinessUnits ^ isConnections;
    }),
  businessUnits: yupArray()
    .test('Business Units', 'You must select Business Units or Connections but not both', (value, testContext) => {
      const isBusinessUnits = !!value?.length;
      const isConnections = !!testContext.parent.connections?.length;
      return isBusinessUnits ^ isConnections;
    }),
  countryTaxTypes: yupArray()
    .of(yupString()),
  description: yupString()
    .required(ERROR_MESSAGES.required('Description')),
  disallowedPercentage: yupNumber()
    .min(-1, 'Disallowed Percentage should be between -100% and 100%')
    .max(1, 'Disallowed Percentage should be between -100% and 100%')
    .required(ERROR_MESSAGES.required('Disallowed Percentage')),
  enabledFlag: yupString()
    .oneOf(['Y', 'N'], ERROR_MESSAGES.oneOf('Enabled Flag'))
    .required(ERROR_MESSAGES.required('Enabled Flag')),
  exceptionAccount: yupString()
    .required(ERROR_MESSAGES.required('Exception Account')),
  exceptionBasis: yupString()
    .oneOf(exceptionBasisValues, ERROR_MESSAGES.oneOf('Exception Basis'))
    .required(ERROR_MESSAGES.required('Exception Basis')),
  exceptionId: yupNumber()
    .nullable(),
  excludedCompanyCodes: yupArray()
    .of(yupString()),
  tagDescription: yupString()
    .nullable(),
});

const createExceptionBasisValidCountryTaxTypesMap = allCountryTaxTypes => {
  const countryTaxTypesWithoutState = allCountryTaxTypes.filter(({ countryTaxType }) =>
    !(US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType)) || countryTaxType === US_FEDERAL
  );

  const countryTaxTypesNonUS = allCountryTaxTypes.filter(({ countryTaxType }) =>
    !US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType)
  );
  const countryTaxTypesUSFederalOnly = [{ countryTaxType: US_FEDERAL }];
  const countryTaxTypesUSStateOnly = allCountryTaxTypes.filter(({ countryTaxType }) =>
    US_COUNTRY_TAX_TYPE_REGEX.test(countryTaxType) && countryTaxType !== US_FEDERAL
  );
  return ({
    S: {
      countryTaxTypes: countryTaxTypesUSFederalOnly,
      message: 'Only US Federal country tax type is assignable for exception basis S',
    },
    P: {
      countryTaxTypes: countryTaxTypesWithoutState,
      message: 'Only Non US or US Federal country tax types are assignable for exception basis P',
    },
    T: {
      countryTaxTypes: countryTaxTypesWithoutState,
      message: 'Only Non US or US Federal country tax types are assignable for exception basis T',
    },
    O: {
      countryTaxTypes: countryTaxTypesNonUS,
      message: 'Only Non US country tax types are assignable for exception basis O',
    },
    G: {
      countryTaxTypes: countryTaxTypesNonUS,
      message: 'Only Non US country tax types are assignable for exception basis G',
    },
    C: {
      countryTaxTypes: allCountryTaxTypes,
    },
    D: {
      countryTaxTypes: allCountryTaxTypes,
    },
    M: {
      countryTaxTypes: countryTaxTypesUSStateOnly,
      message: 'Only US State country tax types are assignable for exception basis M',
    },
    N: {
      countryTaxTypes: countryTaxTypesUSStateOnly,
      message: 'Only US State country tax types are assignable for exception basis N',
    },
  });
};

const validateCountryTaxTypes = (bookToTaxDifference, context) => {
  const { exceptionBasis, countryTaxTypes } = bookToTaxDifference;
  if (!exceptionBasis) {
    return {};
  }
  if (countryTaxTypes.length === 0) {
    return context.countryTaxTypes.reduce((acc, { countryTaxType: invalidCountryTaxType }) => {
      acc[invalidCountryTaxType] = [
        `At least one country tax type need to be assigned`,
      ];
      return acc;
    }, {
      includeAllApplicableCountryTaxTypes: [
        `At least one country tax type need to be assigned`,
      ],
    });
  }
  if (countryTaxTypes.length === 1 && countryTaxTypes[0] === ALL_OPTION) {
    return {};
  }

  const allCountryTaxTypes = context.countryTaxTypes;
  const exceptionBasisValidCountryTaxTypesMap = createExceptionBasisValidCountryTaxTypesMap(allCountryTaxTypes);
  if (!exceptionBasisValidCountryTaxTypesMap[exceptionBasis]) {
    return {};
  }
  const validCountryTaxTypes = exceptionBasisValidCountryTaxTypesMap[exceptionBasis].countryTaxTypes;
  const additionalInfo = exceptionBasisValidCountryTaxTypesMap[exceptionBasis].message;

  const invalidCountryTaxTypes = countryTaxTypes.filter(
    countryTaxType => !validCountryTaxTypes.some(
      ({ countryTaxType: validCountryTaxType }) => countryTaxType === validCountryTaxType
    )
  );

  return invalidCountryTaxTypes.reduce((acc, invalidCountryTaxType) => {
    acc[invalidCountryTaxType] = [
      `${invalidCountryTaxType} is not assignable, when exception basis is ${exceptionBasis}. ${additionalInfo}`,
    ];
    return acc;
  }, {});
};

const validateBookToTaxCodeDescription = (bookToTaxDifference, context) => {
  const { tagDescription, countryTaxTypes, exceptionAccount, exceptionBasis } = bookToTaxDifference;
  const { bookToTaxCodeEnabledFlag } = context;

  const isRequired = bookToTaxCodeEnabledFlag
    && exceptionAccount && !exceptionAccount.startsWith('STMA')
    && (exceptionBasis === 'T' || exceptionBasis === 'P')
    && (countryTaxTypes.includes(US_FEDERAL) || countryTaxTypes.includes(ALL_OPTION));

  if (isRequired && !tagDescription) {
    return {
      tagDescription: ['Book To Tax Code Description is required'],
    };
  }
  return {};
};

const validateBaseSchema = bookToTaxDifference => {
  try {
    bookToTaxDifferenceSchema.validateSync(bookToTaxDifference, {
      abortEarly: false,
      strict: true,
    });
    return {};
  } catch (error) {
    const errorObj = error.inner.reduce((acc, { errors, params }) => {
      acc[params.path] = errors;
      return acc;
    }, {});

    return {
      ...errorObj,
    };
  }
};


const checkEntityForDuplicate = (
  bookToTaxDifference,
  _,
  allBookToTaxDifferences
) => {
  const candidates = allBookToTaxDifferences.filter(entity => (
    entity.exceptionBasis === bookToTaxDifference.exceptionBasis
    && entity.exceptionAccount === bookToTaxDifference.exceptionAccount
    && entity.disallowedPercentage === bookToTaxDifference.disallowedPercentage
    && entity.exceptionId !== bookToTaxDifference.exceptionId
  ));

  const hasAllOption = bookToTaxDifference.countryTaxTypes.some(countryTaxType => countryTaxType === ALL_OPTION);

  const restCountryTaxTypes = uniq(flatten(candidates.map(({ countryTaxTypes }) => countryTaxTypes)));
  const restHasAllOption = restCountryTaxTypes.some(countryTaxType => countryTaxType === ALL_OPTION);

  const commonCountryTaxTypesWithExisting = intersection(
    restCountryTaxTypes,
    bookToTaxDifference.countryTaxTypes
  );

  if (hasAllOption && restCountryTaxTypes.length > 0) {
    return {
      includeAllApplicableCountryTaxTypes: [
        'Conflicting with country tax types of entry with same Exception Account and Basis',
      ],
    };
  }
  if (restHasAllOption && bookToTaxDifference.countryTaxTypes.length > 0) {
    return bookToTaxDifference.countryTaxTypes.reduce((acc, invalidCountryTaxType) => {
      acc[invalidCountryTaxType] = [
        `${invalidCountryTaxType} is conflicting with country tax types of entry with same Exception Account, Basis and Disallowed Percentage`,
      ];
      return acc;
    }, {});
  }
  if (commonCountryTaxTypesWithExisting.length > 0) {
    return commonCountryTaxTypesWithExisting.reduce((acc, invalidCountryTaxType) => {
      acc[invalidCountryTaxType] = [
        `${invalidCountryTaxType} is conflicting with country tax types of entry with same Exception Account, Basis and Disallowed Percentage`,
      ];
      return acc;
    }, {});
  }

  return {};
};

export const validateBookToTaxDifference = (bookToTaxDifference, context, allBookToTaxDifferences) => {
  const baseError = validateBaseSchema(bookToTaxDifference);
  const countryTaxTypesError = validateCountryTaxTypes(bookToTaxDifference, context);
  const bookToTaxCodeDescriptionError = validateBookToTaxCodeDescription(bookToTaxDifference, context);
  const duplicateError = checkEntityForDuplicate(bookToTaxDifference, context, allBookToTaxDifferences);

  return {
    errors: merge({}, baseError, countryTaxTypesError, bookToTaxCodeDescriptionError, duplicateError),
  };
};
